import { ChartingSectionType, ChartingFailureRelatedIDs } from '@caresend/types';
import { trackChartingDone } from '../../../../helpers/tracking.js';
import { usePortalStore } from '../../../../store/portal/index.js';
import { getChartingResultInputs } from '../../../../../../../functions/bookings.js';
import { buildProcedureFailedFromCharting, shouldShowMaybeConditionalItem } from '../../../../../../../functions/charting.js';

/** Helper function - simply gets the failure reason items from the charting items */
const getFailureReasonItems = (chartingItems) => {
    var _a, _b;
    return (_b = (_a = chartingItems.find((item) => item.id === ChartingFailureRelatedIDs.CHARTING_FAILURE_REASON)) === null || _a === void 0 ? void 0 : _a.children) !== null && _b !== void 0 ? _b : [];
};
/**
 * Handles the side effects of a charting failure - i.e. updating the procedure failed object
 *
 * @param chartingItems - The charting items to check for failure reason items.
 * @param procedure - The procedure to update the failed object of.
 * @param fail - Whether the procedure should be marked as failed.
 */
const handleChartingFailureSideEffects = (chartingItems, procedure, fail) => {
    if (!procedure) {
        console.error('Cannot handle charting failure because procedure is undefined');
        return;
    }
    const failureReasonItems = getFailureReasonItems(chartingItems);
    const procedureFailedObj = fail ? buildProcedureFailedFromCharting(failureReasonItems) : undefined;
    // Don't update empty failure to empty failure
    if (!procedure.failed && !procedureFailedObj)
        return;
    const portalStore = usePortalStore();
    portalStore.procedures.setChildOf(procedure.id, 'failed', procedureFailedObj, `Updated procedure ${procedure.id} failed object`);
};
/**
 * Track the completion status of the given results charting items.
 */
const trackChartingItemsAreDone = (chartingItems, waypointActionID, procedureID) => {
    const resultsChartingItems = getChartingResultInputs(chartingItems);
    resultsChartingItems === null || resultsChartingItems === void 0 ? void 0 : resultsChartingItems.forEach((item) => {
        var _a;
        trackChartingDone(waypointActionID, procedureID, (_a = item.productID) !== null && _a !== void 0 ? _a : '', item.selection);
    });
};
/**
 * Given a map of highlighted items split by section and original index + index of the charting item
 * groups (to have access to the DOM element), scroll to the first group that is highlighted.
 */
const scrollToFirstHighlightedItem = (groupIsHighlightedBySectionAndIndex, groupElementsBySectionAndIndex) => {
    let finalSectionType = ChartingSectionType.CLINICAL_NOTES;
    let groupIndex = -1;
    /**
     * Find the first section that has a highlighted item. Set the found section type and group index
     * and end execution early
     */
    Object.entries(groupIsHighlightedBySectionAndIndex).some(([sType, groupIsHighlighted]) => {
        const sectionType = sType;
        const highlightIndex = groupIsHighlighted.findIndex((isHighlighted) => isHighlighted);
        if (highlightIndex !== -1) {
            groupIndex = highlightIndex;
            finalSectionType = sectionType;
            return true;
        }
        return false;
    });
    const elementToScrollTo = groupElementsBySectionAndIndex[finalSectionType][groupIndex];
    if (elementToScrollTo === null || elementToScrollTo === void 0 ? void 0 : elementToScrollTo.$el) {
        elementToScrollTo.$el.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
        });
    }
};
/**
 * Returns true if the group represented by the given item should be highlighted.
 * Takes the conditionalfollow-ups and recursive highlight checks into consideration
 */
const getShouldHighlightItem = (conditionalFollowUpDisplayMap, item, highlightItem) => {
    if (!shouldShowMaybeConditionalItem(conditionalFollowUpDisplayMap, item))
        return false;
    const getItemPairs = (items, highlightItems) => {
        if (items.length !== highlightItems.length) {
            throw new Error('Items and highlight items must have the same length');
        }
        return items.map((it, idx) => [it, highlightItems[idx]]);
    };
    /**
     * Maybe performs a recursive check on any children items only on shown items.
     */
    const recursiveHighlightCheck = (items) => {
        const shownItems = items.filter(([it, _]) => shouldShowMaybeConditionalItem(conditionalFollowUpDisplayMap, it));
        const areChildrenHighlighted = shownItems.some(([it, hi]) => it.children && hi.children && recursiveHighlightCheck(getItemPairs(it.children, hi.children)));
        return shownItems.some(([_, hi]) => hi.highlight) || areChildrenHighlighted;
    };
    if (item.children && highlightItem.children) {
        return recursiveHighlightCheck(getItemPairs(item.children, highlightItem.children));
    }
    return !!highlightItem.highlight;
};
/**
 * Sets the charting items for the given procedure w/ database update
 * - helper since it's used in multiple places in the component
 */
const setChartingItems = (procedureID, chartingItems) => {
    const portalStore = usePortalStore();
    portalStore.procedures.setChildOf(procedureID, 'charting/items', chartingItems, `Updating charting items for procedure ${procedureID}`);
};
/**
 * Returns an array of booleans indicating whether the given items are highlighted
 */
const getAreGroupsHighlightedForSection = (items, chartingHighlightItems, conditionalFollowUpDisplayMap) => items.map(({ item, index }) => {
    const highlightItem = chartingHighlightItems[index];
    if (!highlightItem) {
        console.error('Highlight item not found for item', item.title);
        return false;
    }
    return getShouldHighlightItem(conditionalFollowUpDisplayMap, item, highlightItem);
});

export { getAreGroupsHighlightedForSection, handleChartingFailureSideEffects, scrollToFirstHighlightedItem, setChartingItems, trackChartingItemsAreDone };
